import React, { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Card,
  Box,
  CardMedia,
  CircularProgress,
  Divider,
  CardContent,
  Typography,
  Button,
} from "@mui/material";
import DOMPurify from 'dompurify'; // 1. IMPORT DOMPURIFY

export default function NewsCardItem({ item, newsData }) {
  const [imgLoaded, setImgLoaded] = useState(false);

  // Dynamic image source logic
  const displayImage = item.image || item.cardPic || item.mainImage;

  return (
    <Card
      sx={{
        overflow: "hidden",
        boxShadow: "0 8px 20px rgba(0,0,0,0.3)",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#ffffff",
        borderRadius: "4px", 
      }}
    >
      {/* --- IMAGE CONTAINER WITH FADE EFFECT --- */}
      <Box
        sx={{
          position: "relative",
          height: 350,
          width: "100%",
          overflow: "hidden",
          backgroundColor: "#f5f5f5",
        }}
      >
        {/* 1. SPINNER (Shows while image is loading) */}
        {!imgLoaded && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1,
            }}
          >
            <CircularProgress size={30} sx={{ color: "#009fb6" }} />
          </Box>
        )}

        {/* 2. BACKGROUND BLUR  */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage: `url(${displayImage})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "blur(6px)",
            transform: "scale(1.2)",
            zIndex: 0,
            opacity: imgLoaded ? 1 : 0,
            transition: "opacity 0.8s ease-in-out",
          }}
        />

        {/* 3. ACTUAL FOREGROUND IMAGE */}
        <CardMedia
          component="img"
          src={displayImage}
          alt={item.title || item.cardTitle}
          onLoad={() => setImgLoaded(true)}
          sx={{
            height: "100%",
            width: "100%",
            objectFit: "contain",
            position: "relative",
            zIndex: 2,
            opacity: imgLoaded ? 1 : 0,
            transition: "opacity 0.8s ease-in-out",
          }}
        />
      </Box>

      <Divider sx={{ borderBottomWidth: 3, borderColor: "#5f615bff", mt: 0 }} />

      {/* --- CARD CONTENT --- */}
      <CardContent
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          textAlign: "left",
          mt: 0,
          backgroundColor: "#ffffff",
          padding: '12px 16px'
        }}
      >
        <Box>
          <Typography
            sx={{
              fontWeight: 700,
              mb: 1,
              color: "#7da242",
              fontSize: 15,
            }}
          >
            {item.title || item.cardTitle}
          </Typography>

          {/* 2. CRITICAL FIX: Safe HTML Rendering for the Description Preview */}
          <Typography
            component="div" // Prevent <p> in <p> DOM errors
            sx={{
              mb: 2,
              fontSize: 14,
              fontWeight: 500,
              letterSpacing: 1,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              textOverflow: "ellipsis",
              height: "3.2em",
              color: 'text.secondary',
              // Clean up Tiptap's default paragraph margins so it aligns properly
              '& p': { margin: 0, padding: 0 }
            }}
            dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(item.desc || item.cardDesc || "No description provided.")
            }}
          />
        </Box>

        <Button
          component={RouterLink}
           to="/about/news_details"
          state={{ news: item, newsData: newsData }}
          variant="text"
          disableRipple
          sx={{
            color: "#7da242",
            fontWeight: 600,
            fontSize: "14px",
            textTransform: "uppercase",
            paddingLeft: 0,
            textAlign: "left",
            justifyContent: "flex-start",
            "&:hover": {
              backgroundColor: "transparent",
              color: "#439cf5ff",
            },
          }}
        >
          Read More →
        </Button>
      </CardContent>
    </Card>
  );
}